import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Users, ShoppingBag, MapPin, Bell, Globe2, BadgeCheck, Sparkles, ArrowRight } from "lucide-react";

const Stat = ({ icon: Icon, value, label, hint, testid }) => (
  <div className="bg-white border border-black/10 rounded-2xl p-7" data-testid={testid}>
    <Icon className="w-6 h-6 text-[#C05A3C] mb-4"/>
    <div className="font-display text-5xl tracking-tight tabular-nums">{value}</div>
    <div className="label-overline text-muted-foreground mt-2">{label}</div>
    {hint && <div className="text-xs text-muted-foreground mt-2 leading-snug">{hint}</div>}
  </div>
);

const PublicStats = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/public").then((r) => setStats(r.data)); }, []);

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]"/>
      </div>
    );
  }

  const fmt = (n) => Number(n).toLocaleString();

  return (
    <div className="bg-[#FAF8F5] min-h-screen py-16">
      <div className="container-narrow">
        <div className="text-center mb-12">
          <div className="label-overline text-[#C05A3C] mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3"/> Marketplace transparency
          </div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight mb-4">By the numbers</h1>
          <p className="text-muted-foreground max-w-xl mx-auto font-sans-ui">
            Live, public stats on what's happening across AfriMarket. Updated every visit. Nothing to hide.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Stat icon={Users} value={fmt(stats.users)} label="Registered users" hint="Buyers + sellers signed up" testid="stat-users"/>
          <Stat icon={BadgeCheck} value={fmt(stats.sellers)} label="Verified sellers" hint="KYC-approved by our admin team" testid="stat-sellers"/>
          <Stat icon={ShoppingBag} value={fmt(stats.listings)} label="Live listings" hint={`${fmt(stats.products)} products · ${fmt(stats.services)} services`} testid="stat-listings"/>
          <Stat icon={Bell} value={fmt(stats.saved_searches)} label="Saved searches" hint="Buyers actively waiting for new matches" testid="stat-saved"/>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-12">
          <div className="bg-[#1A2024] text-white rounded-2xl p-8" data-testid="stat-countries">
            <Globe2 className="w-6 h-6 text-[#D9923B] mb-4"/>
            <div className="font-display text-4xl tabular-nums">{stats.countries.length}</div>
            <div className="label-overline text-white/60 mt-2 mb-5">Countries</div>
            <div className="flex flex-wrap gap-2">
              {stats.countries.map((c) => (
                <span key={c} className="text-xs bg-white/10 px-3 py-1 rounded-full">{c}</span>
              ))}
            </div>
          </div>
          <div className="bg-white border border-black/10 rounded-2xl p-8" data-testid="stat-cities">
            <MapPin className="w-6 h-6 text-[#C05A3C] mb-4"/>
            <div className="font-display text-4xl tabular-nums">{stats.cities.length}</div>
            <div className="label-overline text-muted-foreground mt-2 mb-5">Cities active</div>
            <div className="flex flex-wrap gap-2">
              {stats.cities.map((c) => (
                <span key={c} className="text-xs bg-black/5 px-3 py-1 rounded-full">{c}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Founder spots counter */}
        <div className="bg-gradient-to-br from-[#D9923B] to-[#C05A3C] rounded-2xl p-8 text-white text-center" data-testid="stat-founders">
          <div className="label-overline text-white/80 mb-2">Founder Pro spots</div>
          <div className="font-display text-6xl tabular-nums">{stats.founders_remaining}<span className="text-white/60 text-3xl">/100</span></div>
          <p className="text-white/85 mt-3 mb-5 max-w-md mx-auto">
            Lock in <strong>£4.99/mo forever</strong> as one of AfriMarket's first 100 founding sellers. Once they're claimed, the rate doubles.
          </p>
          <Link to="/pro">
            <Button className="bg-white text-[#C05A3C] hover:bg-white/90 rounded-md" data-testid="stat-founders-cta">
              Claim a founder spot <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
          </Link>
        </div>

        <div className="mt-12 text-center text-xs text-muted-foreground">
          Press / investors — for the full revenue dashboard contact <a href="mailto:hello@afrimarket.app" className="text-[#C05A3C]">hello@afrimarket.app</a>.
        </div>
      </div>
    </div>
  );
};

export default PublicStats;
